import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Button, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import PageTitle from "../../../../layouts/PageTitle";
import {
    listConversations,
    listMessageLogs,
    closeConversation,
    reopenConversation,
    takeOverConversation,
    returnToBotConversation,
    sendConversationMessage,
} from "../../../../../services/WhatsappService";
import { errorAlert, successAlert, warnAlertYesOrNo } from "../../../../components/alert/defaultAlert";
import { formatDateTime, formatTimeAgo, parseDateTime } from "../../../../../utils/DateUtils";
import { formatPhoneNumberOfPhoneComponent } from "../../../../../utils/FormatUtils";

const sortMessagesByEventAt = (messages) => (
    [...messages].sort((a, b) => {
        const dateA = parseDateTime(a.eventAt);
        const dateB = parseDateTime(b.eventAt);
        if (!dateA) return dateB ? 1 : 0;
        if (!dateB) return -1;
        return dateA - dateB;
    })
);

const WhatsappConversationList = () => {
    const { t } = useTranslation();
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [conversationLoadError, setConversationLoadError] = useState("");
    const [lastConversationsSync, setLastConversationsSync] = useState(null);
    const [query, setQuery] = useState("");
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [notificationsEnabled, setNotificationsEnabled] = useState(false);
    const [conversationMessages, setConversationMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [messageLoadError, setMessageLoadError] = useState("");
    const [messageDraft, setMessageDraft] = useState("");
    const [messageSending, setMessageSending] = useState(false);

    const lastMessageTimestamps = useRef({});
    const isFirstLoad = useRef(true);
    const containerRef = useRef(null);
    const messagesEndRef = useRef(null);
    const messageRequestRef = useRef(0);
    const audioContextRef = useRef(null);
    const lastNotificationSoundRef = useRef(0);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const selectedConversationId = selectedConversation?.id;

    const requestNotificationPermission = useCallback(async () => {
        if (!("Notification" in window)) {
            return false;
        }

        if (Notification.permission === "granted") {
            setNotificationsEnabled(true);
            return true;
        }

        if (Notification.permission !== "denied") {
            const permission = await Notification.requestPermission();
            if (permission === "granted") {
                setNotificationsEnabled(true);
                return true;
            }
        }
        return false;
    }, []);

    const sendNotification = useCallback((conversation) => {
        if (Notification.permission === "granted") {
            const notification = new Notification("Nova mensagem WhatsApp", {
                body: `${conversation.customerName || formatPhoneNumberOfPhoneComponent(conversation.customerWaId)} enviou uma mensagem`,
                icon: "/favicon.ico",
                tag: `whatsapp-${conversation.id}`,
                requireInteraction: false,
            });

            notification.onclick = () => {
                window.focus();
                setSelectedConversation(conversation);
                notification.close();
            };

            setTimeout(() => notification.close(), 5000);
        }
    }, []);

    const playNotificationSound = useCallback(() => {
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextClass) {
                return;
            }

            let audioContext = audioContextRef.current;
            if (!audioContext || audioContext.state === "closed") {
                audioContext = new AudioContextClass();
                audioContextRef.current = audioContext;
            }

            if (audioContext.state === "suspended") {
                audioContext.resume().catch(() => {});
            }

            const now = audioContext.currentTime;
            if (now - lastNotificationSoundRef.current < 0.45) {
                return;
            }
            lastNotificationSoundRef.current = now;

            const masterGain = audioContext.createGain();
            masterGain.gain.setValueAtTime(0.0001, now);
            masterGain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
            masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.9);
            masterGain.connect(audioContext.destination);

            const scheduleTone = (frequency, startOffset, duration, type, volume, detune = 0) => {
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();

                oscillator.type = type;
                oscillator.frequency.setValueAtTime(frequency, now + startOffset);
                oscillator.detune.setValueAtTime(detune, now + startOffset);

                gainNode.gain.setValueAtTime(0.0001, now + startOffset);
                gainNode.gain.exponentialRampToValueAtTime(volume, now + startOffset + 0.025);
                gainNode.gain.exponentialRampToValueAtTime(0.0001, now + startOffset + duration);

                oscillator.connect(gainNode);
                gainNode.connect(masterGain);

                oscillator.start(now + startOffset);
                oscillator.stop(now + startOffset + duration + 0.03);
            };

            scheduleTone(784, 0, 0.22, "triangle", 0.55);
            scheduleTone(1174.66, 0.02, 0.18, "sine", 0.18, 6);
            scheduleTone(1046.5, 0.16, 0.34, "triangle", 0.42);
            scheduleTone(1567.98, 0.18, 0.28, "sine", 0.14, 4);

            setTimeout(() => {
                masterGain.disconnect();
            }, 1200);
        } catch {
            // Silenciosamente ignora erro de som
        }
    }, []);

    const checkForNewMessages = useCallback((newConversations) => {
        if (isFirstLoad.current) {
            newConversations.forEach((conv) => {
                if (conv.lastMessageAt) {
                    lastMessageTimestamps.current[conv.id] = conv.lastMessageAt;
                }
            });
            isFirstLoad.current = false;
            return;
        }

        newConversations.forEach((conv) => {
            const previousTimestamp = lastMessageTimestamps.current[conv.id];
            const currentTimestamp = conv.lastMessageAt;

            if (currentTimestamp) {
                const isNewConversation = !previousTimestamp;
                const hasNewMessage = previousTimestamp && currentTimestamp !== previousTimestamp;

                if (isNewConversation || hasNewMessage) {
                    playNotificationSound();
                    sendNotification(conv);
                }
                lastMessageTimestamps.current[conv.id] = currentTimestamp;
            }
        });
    }, [sendNotification, playNotificationSound]);

    const loadData = useCallback(({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        setConversationLoadError("");
        listConversations()
            .then((resp) => {
                const newConversations = resp.data || [];
                checkForNewMessages(newConversations);
                setConversations(newConversations);
                setLastConversationsSync(new Date());
                setSelectedConversation((previousSelectedConversation) => {
                    if (!previousSelectedConversation?.id) {
						return null;
                    }
                    return newConversations.find((conversation) => conversation.id === previousSelectedConversation.id)
                        || null;
                });
            })
            .catch(() => setConversationLoadError(t("whatsapp_conversations_load_failed")))
            .finally(() => {
                if (!silent) setLoading(false);
            });
    }, [checkForNewMessages, t]);

    const scrollToBottom = useCallback((behavior = "smooth") => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior });
        }
    }, []);

    const loadConversationMessages = useCallback((conversationId, options = {}) => {
        const { silent = false } = options;

        if (!conversationId) {
            setConversationMessages([]);
            setMessagesLoading(false);
            setMessageLoadError("");
            return Promise.resolve();
        }

        const requestId = messageRequestRef.current + 1;
        messageRequestRef.current = requestId;

        if (!silent) {
            setMessagesLoading(true);
            setMessageLoadError("");
        }

        return listMessageLogs([{
            field: "conversation.id",
            value: conversationId,
            operation: "EQUAL"
        }])
            .then((resp) => {
                if (messageRequestRef.current !== requestId) {
                    return;
                }
                setConversationMessages(sortMessagesByEventAt(resp.data || []));
                setMessageLoadError("");
            })
            .catch(() => {
                if (messageRequestRef.current !== requestId) {
                    return;
                }
                setMessageLoadError(t("whatsapp_conversations_messages_load_failed"));
            })
            .finally(() => {
                if (messageRequestRef.current === requestId && !silent) {
                    setMessagesLoading(false);
                }
            });
    }, [t]);

    useEffect(() => {
        requestNotificationPermission();
        loadData();
    }, [loadData, requestNotificationPermission]);

    useEffect(() => {
        const interval = setInterval(() => {
            loadData({ silent: true });
        }, 10000);

        return () => clearInterval(interval);
    }, [loadData]);

    useEffect(() => {
        if (!selectedConversationId) {
            setConversationMessages([]);
            setMessageDraft("");
            return;
        }

        setMessageDraft("");
        loadConversationMessages(selectedConversationId);
    }, [loadConversationMessages, selectedConversationId]);

    useEffect(() => {
        if (!selectedConversationId) {
            return undefined;
        }

        const interval = setInterval(() => {
            loadConversationMessages(selectedConversationId, { silent: true });
        }, 10000);

        return () => clearInterval(interval);
    }, [loadConversationMessages, selectedConversationId]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === containerRef.current);
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);
        return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
    }, []);

    useEffect(() => {
        if (!selectedConversationId || conversationMessages.length === 0) {
            return;
        }

        const timeoutId = setTimeout(() => {
            scrollToBottom("auto");
        }, 80);

        return () => clearTimeout(timeoutId);
    }, [conversationMessages, scrollToBottom, selectedConversationId]);

    const stats = useMemo(() => {
        const open = conversations.filter((c) => c.conversationStatus === "OPEN").length;
        const waitingHuman = conversations.filter((c) => c.conversationStatus === "WAITING_HUMAN").length;
        const inAttendance = conversations.filter((c) => c.conversationStatus === "IN_ATTENDANCE").length;
        const closed = conversations.filter((c) => c.conversationStatus === "CLOSED").length;
        return {
            total: conversations.length,
            open,
            waitingHuman,
            inAttendance,
            closed
        };
    }, [conversations]);

    const filtered = useMemo(() => {
        let result = conversations;

        if (statusFilter === "open") {
            result = result.filter((c) => c.conversationStatus === "OPEN");
        } else if (statusFilter === "waiting_human") {
            result = result.filter((c) => c.conversationStatus === "WAITING_HUMAN");
        } else if (statusFilter === "in_attendance") {
            result = result.filter((c) => c.conversationStatus === "IN_ATTENDANCE");
        } else if (statusFilter === "closed") {
            result = result.filter((c) => c.conversationStatus === "CLOSED");
        }

        if (query) {
            const q = query.toLowerCase();
            result = result.filter((c) =>
                (c.customerName || "").toLowerCase().includes(q)
                || (c.customerWaId || "").toLowerCase().includes(q)
                || (c.channelNumber || "").toLowerCase().includes(q)
                || (c.bot?.name || "").toLowerCase().includes(q)
            );
        }

        return result.sort((a, b) => {
            const dateA = parseDateTime(a.lastMessageAt);
            const dateB = parseDateTime(b.lastMessageAt);
            if (!dateA) return dateB ? 1 : 0;
            if (!dateB) return -1;
            return dateB - dateA;
        });
    }, [conversations, query, statusFilter]);

    const groupedMessages = useMemo(() => {
        const groups = {};

        conversationMessages.forEach((log) => {
            const eventDate = parseDateTime(log.eventAt);
            if (!eventDate) {
                return;
            }

            const dateKey = eventDate.toLocaleDateString("pt-BR", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric"
            });

            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }

            groups[dateKey].push(log);
        });

        return groups;
    }, [conversationMessages]);

    const handleToggleFullscreen = useCallback(async () => {
        if (!containerRef.current || !document.fullscreenEnabled) {
            return;
        }

        try {
            if (document.fullscreenElement === containerRef.current) {
                await document.exitFullscreen();
            } else {
                await containerRef.current.requestFullscreen();
            }
        } catch {
            // Silenciosamente ignora falhas da Fullscreen API
        }
    }, []);

    const getConversationStatusClass = useCallback((status) => {
        if (status === "OPEN") return "open";
        if (status === "WAITING_HUMAN") return "waiting-human";
        if (status === "IN_ATTENDANCE") return "in-attendance";
        return "closed";
    }, []);

    const getConversationStatusLabel = useCallback((status) => {
        if (status === "OPEN") return t("open");
        if (status === "WAITING_HUMAN") return t("waiting_human");
        if (status === "IN_ATTENDANCE") return t("in_attendance");
        return t("closed");
    }, [t]);

    const getConversationStatusIcon = useCallback((status) => {
        if (status === "OPEN") return "fa-comment-dots";
        if (status === "WAITING_HUMAN") return "fa-user-clock";
        if (status === "IN_ATTENDANCE") return "fa-user-check";
        return "fa-check-circle";
    }, []);

    const renderConversationStatusBadge = useCallback((status, extraClass = "") => (
        <span className={`conversation-status-chip ${getConversationStatusClass(status)} ${extraClass}`.trim()}>
            <i className={`fa ${getConversationStatusIcon(status)}`}></i>
            {getConversationStatusLabel(status)}
        </span>
    ), [getConversationStatusClass, getConversationStatusIcon, getConversationStatusLabel]);

    const renderHeaderActionButton = useCallback(({ key, variant, icon, label, onClick, className = "" }) => (
        <OverlayTrigger
            key={key}
            placement="top"
            overlay={<Tooltip className="conversation-action-tooltip">{label}</Tooltip>}
        >
            <Button
                variant={variant}
                className={`header-action-btn icon-only ${className}`.trim()}
                onClick={onClick}
                aria-label={label}
            >
                <i className={`fa ${icon}`}></i>
            </Button>
        </OverlayTrigger>
    ), []);

    const getInitials = useCallback((name) => {
        if (!name) return "?";
        const parts = name.split(" ").filter(Boolean);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    }, []);

    const getAvatarColor = useCallback((name) => {
        const colors = [
            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
            "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
            "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
            "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
            "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
            "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
        ];
        if (!name) return colors[0];
        const index = name.charCodeAt(0) % colors.length;
        return colors[index];
    }, []);

    const renderAvatar = useCallback((name, profilePicUrl, sizeClass = "avatar") => {
        if (profilePicUrl) {
            return (
                <div className={sizeClass} style={{ background: getAvatarColor(name) }}>
                    <img
                        src={profilePicUrl}
                        alt={name || ""}
                        onError={(e) => {
                            e.currentTarget.style.display = "none";
                            e.currentTarget.parentElement.classList.add("avatar-initials-fallback");
                            e.currentTarget.parentElement.setAttribute("data-initials", getInitials(name));
                        }}
                    />
                </div>
            );
        }
        return (
            <div className={sizeClass} style={{ background: getAvatarColor(name) }}>
                {getInitials(name)}
            </div>
        );
    }, [getAvatarColor, getInitials]);

    const handleTakeOverConversation = useCallback((conversation) => {
        takeOverConversation(conversation.id)
            .then((response) => {
                successAlert(t("conversation_taken_over_successfully"));
                loadData();
                setSelectedConversation((prev) => (
                    prev?.id === conversation.id
                        ? { ...prev, conversationStatus: "IN_ATTENDANCE", attendedBy: response.data?.attendedBy }
                        : prev
                ));
            })
            .catch((error) => errorAlert(error));
    }, [loadData, t]);

    const handleReturnToBotConversation = useCallback((conversation) => {
        returnToBotConversation(conversation.id)
            .then(() => {
                successAlert(t("conversation_returned_to_bot_successfully"));
                loadData();
                setSelectedConversation((prev) => (
                    prev?.id === conversation.id
                        ? { ...prev, conversationStatus: "OPEN", attendedBy: null }
                        : prev
                ));
            })
            .catch((error) => errorAlert(error));
    }, [loadData, t]);

    const handleCloseConversation = useCallback((conversation) => {
        warnAlertYesOrNo(
            t("confirm_close_conversation_message"),
            () => {
                closeConversation(conversation.id)
                    .then(() => {
                        successAlert(t("conversation_closed_successfully"));
                        loadData();
                        setSelectedConversation((prev) => (
                            prev?.id === conversation.id ? { ...prev, conversationStatus: "CLOSED" } : prev
                        ));
                    })
                    .catch((error) => errorAlert(error));
            }
        );
    }, [loadData, t]);

    const handleReopenConversation = useCallback((conversation) => {
        reopenConversation(conversation.id)
            .then(() => {
                successAlert(t("conversation_reopened_successfully"));
                loadData();
            })
            .catch((error) => errorAlert(error));
    }, [loadData, t]);

    const handleConversationSelect = useCallback((conversation) => {
        setSelectedConversation(conversation);
    }, []);

    const handleSendMessage = useCallback(() => {
        const trimmed = messageDraft.trim();
        if (!trimmed || !selectedConversationId || messageSending) {
            return;
        }

        setMessageSending(true);
        sendConversationMessage(selectedConversationId, trimmed)
            .then(() => {
                setMessageDraft("");
                loadConversationMessages(selectedConversationId, { silent: true });
                loadData();
            })
            .catch((error) => errorAlert(error))
            .finally(() => setMessageSending(false));
    }, [loadConversationMessages, loadData, messageDraft, messageSending, selectedConversationId]);

    const handleMessageKeyDown = useCallback((event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSendMessage();
        }
    }, [handleSendMessage]);

    const getPayloadObject = useCallback((payload) => {
        if (!payload) return null;
        if (typeof payload === "object") return payload;
        try {
            return JSON.parse(payload);
        } catch {
            return null;
        }
    }, []);

    const getMessageBody = useCallback((log) => {
        const payloadObject = getPayloadObject(log.payload);
        return payloadObject?.text?.body
            || payloadObject?.entry?.[0]?.changes?.[0]?.value?.messages?.[0]?.text?.body
            || log.bodyPreview
            || "-";
    }, [getPayloadObject]);

    const getMessageTypeIcon = useCallback((type) => {
        const icons = {
            text: "fa-comment",
            image: "fa-image",
            audio: "fa-microphone",
            document: "fa-file-alt",
            video: "fa-video",
            sticker: "fa-note-sticky",
            location: "fa-location-dot",
            contact: "fa-address-card"
        };

        return icons[type?.toLowerCase()] || "fa-comment";
    }, []);

    const renderMessageContent = useCallback((log) => {
        const type = log.messageType?.toLowerCase() || "text";
        const messageBody = getMessageBody(log);

        if (type === "text") {
            return <p className="message-text">{messageBody}</p>;
        }

        return (
            <div className="message-media-placeholder">
                <div className="media-icon">
                    <i className={`fa ${getMessageTypeIcon(type)}`}></i>
                </div>
                <div className="media-copy">
                    <span className="media-type">{log.messageType || type}</span>
                    {messageBody && messageBody !== "-" && (
                        <p className="message-caption">{messageBody}</p>
                    )}
                </div>
            </div>
        );
    }, [getMessageBody, getMessageTypeIcon]);

    const formatMessageTime = useCallback((dateString) => {
        const date = parseDateTime(dateString);
        if (!date) return "";
        return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    }, []);

    return (
        <>
            <PageTitle activeMenu={t("whatsapp_conversations")} motherMenu={t("chat_bot")} />

            <div
                ref={containerRef}
                className={`whatsapp-conversation-container ${isFullscreen ? "is-fullscreen" : ""}`}
            >
                <div className="conversation-sidebar">
                    <div className="sidebar-header">
                        <div className="header-title">
                            <i className="fab fa-whatsapp me-2"></i>
                            <span>{t("whatsapp_conversations")}</span>
                            <div className="header-controls ms-auto">
                                <button
                                    className={`fullscreen-toggle ${isFullscreen ? "enabled" : ""}`}
                                    onClick={handleToggleFullscreen}
                                    title={isFullscreen ? t("exit_fullscreen") : t("enter_fullscreen")}
                                >
                                    <i className={`fa ${isFullscreen ? "fa-compress" : "fa-expand"}`}></i>
                                </button>
                                <button
                                    className={`notification-toggle ${notificationsEnabled ? "enabled" : ""}`}
                                    onClick={requestNotificationPermission}
                                    title={notificationsEnabled ? t("notifications_enabled") : t("enable_notifications")}
                                >
                                    <i className={`fa ${notificationsEnabled ? "fa-bell" : "fa-bell-slash"}`}></i>
                                </button>
                            </div>
                        </div>
                        <div className="header-stats">
                            <div className="stat-card open">
                                <i className="fa fa-circle-dot"></i>
                                <span className="stat-number">{stats.open}</span>
                                <span className="stat-label">{t("open")}</span>
                            </div>
                            <div className="stat-card waiting-human">
                                <i className="fa fa-user-clock"></i>
                                <span className="stat-number">{stats.waitingHuman}</span>
                                <span className="stat-label">{t("waiting_human")}</span>
                            </div>
                            <div className="stat-card in-attendance">
                                <i className="fa fa-headset"></i>
                                <span className="stat-number">{stats.inAttendance}</span>
                                <span className="stat-label">{t("in_attendance")}</span>
                            </div>
                            <div className="stat-card closed">
                                <i className="fa fa-check-circle"></i>
                                <span className="stat-number">{stats.closed}</span>
                                <span className="stat-label">{t("closed")}</span>
                            </div>
                        </div>
                    </div>

                    <div className="sidebar-search">
                        <div className="search-input-wrapper">
                            <i className="fa fa-search search-icon"></i>
                            <input
                                type="text"
                                placeholder={t("search_conversations")}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="search-input"
                            />
                            {query && (
                                <button className="clear-search" onClick={() => setQuery("")}>
                                    <i className="fa fa-times"></i>
                                </button>
                            )}
                        </div>
                        <div className="filter-tabs">
                            <button className={`filter-tab ${statusFilter === "all" ? "active" : ""}`} onClick={() => setStatusFilter("all")}>
                                {t("all")} <span className="tab-count">({stats.total})</span>
                            </button>
                            <button className={`filter-tab ${statusFilter === "open" ? "active active-open" : ""}`} onClick={() => setStatusFilter("open")}>
                                {t("open")} <span className="tab-count">({stats.open})</span>
                            </button>
                            <button className={`filter-tab ${statusFilter === "waiting_human" ? "active active-waiting" : ""}`} onClick={() => setStatusFilter("waiting_human")}>
                                {t("waiting_human")} <span className="tab-count">({stats.waitingHuman})</span>
                            </button>
                            <button className={`filter-tab ${statusFilter === "in_attendance" ? "active active-attendance" : ""}`} onClick={() => setStatusFilter("in_attendance")}>
                                {t("in_attendance")} <span className="tab-count">({stats.inAttendance})</span>
                            </button>
                            <button className={`filter-tab ${statusFilter === "closed" ? "active active-closed" : ""}`} onClick={() => setStatusFilter("closed")}>
                                {t("closed")} <span className="tab-count">({stats.closed})</span>
                            </button>
                        </div>
                    </div>

                    <div className="conversation-list">
                        {loading && conversations.length === 0 ? (
                            <div className="loading-state">
                                <Spinner animation="border" variant="success" />
                                <span>{t("loading")}...</span>
                            </div>
                        ) : conversationLoadError && conversations.length === 0 ? (
                            <div className="empty-state conversation-error-state" role="alert">
                                <i className="fa fa-triangle-exclamation"></i>
                                <span>{conversationLoadError}</span>
                                <Button size="sm" variant="outline-secondary" onClick={() => loadData()}>
                                    {t("whatsapp_conversations_retry")}
                                </Button>
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="empty-state">
                                <i className={`fa ${conversations.length === 0 ? "fa-comments" : "fa-magnifying-glass"}`}></i>
                                <span>{conversations.length === 0
                                    ? t("whatsapp_conversations_empty")
                                    : t("whatsapp_conversations_filter_empty")}</span>
                                {conversations.length > 0 && (
                                    <Button size="sm" variant="outline-secondary" onClick={() => {
                                        setQuery("");
                                        setStatusFilter("all");
                                    }}>
                                        {t("whatsapp_conversations_clear_filters")}
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <>
                                {conversationLoadError && (
                                    <div className="conversation-inline-error" role="alert">
                                        <span>{conversationLoadError}</span>
                                        <Button size="sm" variant="link" onClick={() => loadData()}>
                                            {t("whatsapp_conversations_retry")}
                                        </Button>
                                    </div>
                                )}
                                {filtered.map((conversation) => (
                                    <div
                                        key={conversation.id}
                                        className={`conversation-item ${selectedConversationId === conversation.id ? "selected" : ""} ${conversation.conversationStatus === "OPEN" ? "is-open" : ""} ${conversation.conversationStatus === "WAITING_HUMAN" ? "is-waiting-human" : ""} ${conversation.conversationStatus === "IN_ATTENDANCE" ? "is-in-attendance" : ""}`}
                                        onClick={() => handleConversationSelect(conversation)}
                                    >
                                    {renderAvatar(conversation.customerName, conversation.profilePicUrl)}
                                    <div className="conversation-info">
                                        <div className="conversation-header">
                                            <span className="customer-name">
                                                {conversation.customerName || formatPhoneNumberOfPhoneComponent(conversation.customerWaId)}
                                            </span>
                                            <span className="message-time">
                                                {conversation.lastMessageAt ? (
                                                    <time dateTime={parseDateTime(conversation.lastMessageAt)?.toISOString()} title={formatDateTime(conversation.lastMessageAt)}>
                                                        {formatTimeAgo(conversation.lastMessageAt)}
                                                    </time>
                                                ) : ""}
                                            </span>
                                        </div>
                                        <div className="conversation-meta">
                                            <span className="conversation-meta-pill">
                                                <i className="fab fa-whatsapp"></i>
                                                {formatPhoneNumberOfPhoneComponent(conversation.customerWaId)}
                                            </span>
                                            {conversation.channelNumber && (
                                                <span className="conversation-meta-pill muted">
                                                    <i className="fa fa-phone"></i>
                                                    {conversation.channelNumber}
                                                </span>
                                            )}
                                        </div>
                                        <div className="conversation-preview">
                                            <div className="preview-text">
                                                {renderConversationStatusBadge(conversation.conversationStatus)}
                                                {conversation.bot?.name && (
                                                    <span className="bot-badge">
                                                        <i className="fa fa-robot"></i> {conversation.bot.name}
                                                    </span>
                                                )}
                                                {conversation.conversationStatus === "IN_ATTENDANCE" && conversation.attendedBy?.fullName && (
                                                    <span className="attended-by-badge">
                                                        <i className="fa fa-user-check"></i> {conversation.attendedBy.fullName}
                                                    </span>
                                                )}
                                                {conversation.conversationStatus === "WAITING_HUMAN" && (
                                                    <span className="waiting-human-badge">
                                                        <i className="fa fa-user-clock"></i> {t("waiting_human_queue")}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>

                    <div className="sidebar-footer">
                        <button className="sync-button" onClick={() => loadData()} disabled={loading}>
                            <i className={`fa fa-sync-alt ${loading ? "fa-spin" : ""}`}></i>
                            <span>{loading ? t("whatsapp_conversations_syncing") : t("sync")}</span>
                        </button>
                        {lastConversationsSync && (
                            <span className="last-update" title={formatDateTime(lastConversationsSync)}>
                                {t("whatsapp_conversations_updated_ago", { time: formatTimeAgo(lastConversationsSync) })}
                            </span>
                        )}
                    </div>
                </div>

                <div className="conversation-main">
                    {selectedConversation ? (
                        <>
                                <div className="main-header">
                                    <div className="contact-info">
                                        {renderAvatar(selectedConversation.customerName, selectedConversation.profilePicUrl, "avatar large")}
                                        <div className="contact-details">
                                            <div className="contact-topline">
                                                <h4 className="contact-name">
                                                    {selectedConversation.customerName || t("unknown_contact")}
                                                </h4>
                                                {renderConversationStatusBadge(selectedConversation.conversationStatus, "header-status-badge")}
                                            </div>
                                            <div className="contact-meta compact">
                                                <span className="contact-pill">
                                                    <i className="fab fa-whatsapp"></i>
                                                    {formatPhoneNumberOfPhoneComponent(selectedConversation.customerWaId)}
                                                </span>
                                                {selectedConversation.conversationStatus === "IN_ATTENDANCE" && selectedConversation.attendedBy?.fullName && (
                                                    <span className="contact-pill attendance-pill">
                                                        <i className="fa fa-user-check"></i>
                                                        {selectedConversation.attendedBy.fullName}
                                                    </span>
                                                )}
                                                {selectedConversation.conversationStatus === "WAITING_HUMAN" && (
                                                    <span className="contact-pill waiting-pill">
                                                        <i className="fa fa-user-clock"></i>
                                                        {t("waiting_human_queue")}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="contact-meta secondary mt-2">
                                                {selectedConversation.channelNumber && (
                                                    <span className="contact-pill subtle">
                                                        <i className="fa fa-hashtag"></i>
                                                        <span className="pill-label">Canal</span>
                                                        {formatPhoneNumberOfPhoneComponent(selectedConversation.channelNumber)}
                                                    </span>
                                                )}
                                                {selectedConversation.bot?.name && (
                                                    <span className="contact-pill subtle bot-pill">
                                                        <i className="fa fa-robot"></i>
                                                        <span className="pill-label">Bot</span>
                                                        {selectedConversation.bot.name}
                                                    </span>
                                                )}
                                                {selectedConversation.lastMessageAt && (
                                                    <span className="contact-pill subtle time-pill">
                                                        <i className="fa fa-clock"></i>
                                                        <span className="pill-label">Ultima</span>
                                                        {formatDateTime(selectedConversation.lastMessageAt)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                <div className="header-actions">
                                    <div className="header-action-buttons">
                                        {(selectedConversation.conversationStatus === "OPEN" || selectedConversation.conversationStatus === "WAITING_HUMAN") && (
                                            <>
                                                {renderHeaderActionButton({
                                                    key: "take-over",
                                                    variant: "warning",
                                                    icon: "fa-user-check",
                                                    label: t("take_over_conversation"),
                                                    onClick: () => handleTakeOverConversation(selectedConversation)
                                                })}
                                                {selectedConversation.conversationStatus === "WAITING_HUMAN" && renderHeaderActionButton({
                                                    key: "return-to-bot-waiting",
                                                    variant: "secondary",
                                                    icon: "fa-robot",
                                                    label: t("return_to_bot"),
                                                    onClick: () => handleReturnToBotConversation(selectedConversation)
                                                })}
                                                {renderHeaderActionButton({
                                                    key: "close-open",
                                                    variant: "danger",
                                                    icon: "fa-times-circle",
                                                    label: t("close_conversation"),
                                                    onClick: () => handleCloseConversation(selectedConversation)
                                                })}
                                            </>
                                        )}
                                        {selectedConversation.conversationStatus === "IN_ATTENDANCE" && (
                                            <>
                                                {renderHeaderActionButton({
                                                    key: "return-to-bot-attendance",
                                                    variant: "secondary",
                                                    icon: "fa-robot",
                                                    label: t("return_to_bot"),
                                                    onClick: () => handleReturnToBotConversation(selectedConversation)
                                                })}
                                                {renderHeaderActionButton({
                                                    key: "close-attendance",
                                                    variant: "danger",
                                                    icon: "fa-times-circle",
                                                    label: t("close_conversation"),
                                                    onClick: () => handleCloseConversation(selectedConversation)
                                                })}
                                            </>
                                        )}
                                        {selectedConversation.conversationStatus === "CLOSED" && renderHeaderActionButton({
                                            key: "reopen",
                                            variant: "success",
                                            icon: "fa-redo",
                                            label: t("reopen_conversation"),
                                            onClick: () => handleReopenConversation(selectedConversation)
                                        })}
                                    </div>
                                </div>
                            </div>

                            <div className="main-content thread-content">
                                <div className="chat-messages conversation-chat-messages">
                                    {messagesLoading && conversationMessages.length === 0 ? (
                                        <div className="chat-loading">
                                            <Spinner animation="border" variant="success" />
                                            <span>{t("loading_messages")}...</span>
                                        </div>
                                    ) : messageLoadError && conversationMessages.length === 0 ? (
                                        <div className="chat-empty chat-error-state" role="alert">
                                            <i className="fa fa-triangle-exclamation"></i>
                                            <span>{messageLoadError}</span>
                                            <Button size="sm" variant="outline-secondary" onClick={() => loadConversationMessages(selectedConversationId)}>
                                                {t("whatsapp_conversations_retry")}
                                            </Button>
                                        </div>
                                    ) : conversationMessages.length === 0 ? (
                                        <div className="chat-empty">
                                            <i className="fa fa-comments"></i>
                                            <span>{t("no_messages_yet")}</span>
                                        </div>
                                    ) : (
                                        <>
                                            {messageLoadError && (
                                                <div className="conversation-inline-error message-load-warning" role="alert">
                                                    <span>{messageLoadError}</span>
                                                    <Button size="sm" variant="link" onClick={() => loadConversationMessages(selectedConversationId)}>
                                                        {t("whatsapp_conversations_retry")}
                                                    </Button>
                                                </div>
                                            )}
                                            {Object.entries(groupedMessages).map(([date, messages]) => (
                                                <div key={date} className="message-group">
                                                    <div className="date-separator">
                                                        <span>{date}</span>
                                                    </div>
                                                    {messages.map((log) => {
                                                        const isOutbound = log.direction === "OUTBOUND";

                                                        return (
                                                            <div key={log.id} className={`message-wrapper ${isOutbound ? "outbound" : "inbound"}`}>
                                                                <div className="message-bubble">
                                                                    {renderMessageContent(log)}
                                                                    <div className="message-meta">
                                                                        <span className="message-time">{formatMessageTime(log.eventAt)}</span>
                                                                        {isOutbound && (
                                                                            <span className="message-status">
                                                                                <i className="fa fa-check-double"></i>
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ))}
                                            <div ref={messagesEndRef} />
                                        </>
                                    )}
                                </div>
                            </div>

                            {selectedConversation.conversationStatus === "IN_ATTENDANCE" && (
                                <div className="chat-input-area inline-chat-input">
                                    <textarea
                                        className="chat-input"
                                        rows={1}
                                        placeholder={t("type_message")}
                                        value={messageDraft}
                                        onChange={(e) => setMessageDraft(e.target.value)}
                                        onKeyDown={handleMessageKeyDown}
                                        disabled={messageSending}
                                    />
                                    <button
                                        className="chat-send-btn"
                                        onClick={handleSendMessage}
                                        disabled={messageSending || !messageDraft.trim()}
                                        title={t("send")}
                                    >
                                        {messageSending
                                            ? <i className="fa fa-spinner fa-spin"></i>
                                            : <i className="fa fa-paper-plane"></i>}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="no-selection">
                            <div className="no-selection-content">
                                <div className="icon-wrapper">
                                    <i className="fab fa-whatsapp"></i>
                                </div>
                                <h3>{t("whatsapp_conversations")}</h3>
                                <p>{t("select_conversation_to_view")}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default WhatsappConversationList;
